// RhymeBadge - Interactive rhyme badge with hover and click behavior
// Replaces read-only RhymeLabel with interactive 20x20 badge
// Supports hover highlights, click popovers, and keyboard navigation

import { memo, type MouseEvent, type KeyboardEvent } from 'react'
import type { RhymeCategory } from '../../../utils/rhyme/types'
import { CATEGORY_DISPLAY } from '../../../utils/rhyme/categoryDisplay'
import './rhymeBadge.css'

export interface ConnectedRhyme {
  /** Line number in the document (1-indexed) */
  lineNumber: number
  /** The rhyming word */
  word: string
  /** Whether this is the current line */
  isCurrent: boolean
}

interface RhymeBadgeProps {
  /** Unique identifier for this badge */
  id: string
  /** Rhyme scheme letter (A, B, C, etc.) */
  letter: string
  /** X position relative to editor root */
  x: number
  /** Y position relative to editor root */
  y: number
  /** Rhyme category for accessibility and popover */
  category: RhymeCategory
  /** Callback subscript for cross-section callbacks (A₁, A₂, etc.) */
  callbackSubscript?: number
  /** Whether this is a related rhyme (A' vs A) - Scope 22 */
  isRelated?: boolean
  /** Border style based on category strength */
  borderStyle: 'solid' | 'dashed' | 'dotted'
  /** Opacity based on stability expectation match */
  opacity: number
  /** Whether this badge is currently active (popover open) */
  isActive: boolean
  /** Whether this badge is highlighted (group hover) */
  isHighlighted: boolean
  /** Whether this badge is dimmed (another badge is active) */
  isDimmed: boolean
  /** Connected rhymes in this group (for popover) */
  connectedRhymes: ConnectedRhyme[]
  /** Mouse enter handler */
  onMouseEnter: (id: string) => void
  /** Mouse leave handler */
  onMouseLeave: (id: string) => void
  /** Click handler */
  onClick: (id: string) => void
}

/**
 * Custom comparison to prevent unnecessary re-renders.
 * Badges update frequently during text changes - prevent wasteful re-renders.
 */
function arePropsEqual(prev: RhymeBadgeProps, next: RhymeBadgeProps): boolean {
  return (
    prev.id === next.id &&
    prev.letter === next.letter &&
    prev.x === next.x &&
    prev.y === next.y &&
    prev.category === next.category &&
    prev.callbackSubscript === next.callbackSubscript &&
    prev.isRelated === next.isRelated &&
    prev.borderStyle === next.borderStyle &&
    prev.opacity === next.opacity &&
    prev.isActive === next.isActive &&
    prev.isHighlighted === next.isHighlighted &&
    prev.isDimmed === next.isDimmed &&
    prev.connectedRhymes === next.connectedRhymes
  )
}

/**
 * RhymeBadge - Interactive rhyme scheme badge with progressive disclosure
 *
 * Design token reference: docs/design/feedback_system/design_tokens.json
 * Styles from: frontend/src/components/lexical/components/rhymeBadge.css
 * - Size: 20x20px badge (interactive gutter indicator)
 * - Border-radius: 4px
 * - Font-size: 11px (bold 700)
 * - Subscript: 8px (weight 400)
 * - Background color: From design token CSS classes
 * - Border style: solid/dashed/dotted based on category strength
 * - Opacity: 0.4-1.0 based on stability expectation match (inline only)
 *
 * Interaction states:
 * - Hover: Scale 1.1 + shadow
 * - Highlighted: Scale 1.15 + ring (when group member is hovered)
 * - Active: Ring effect (when popover is open)
 * - Dimmed: Opacity 0.3 (when another badge is active)
 *
 * Accessibility:
 * - role="button" with keyboard support (Enter/Space to toggle)
 * - aria-expanded, aria-haspopup for screen readers
 * - aria-label describes category, letter, and stability
 * - data-testid includes letter and category for testing
 */
const RhymeBadge = memo(function RhymeBadge({
  id,
  letter,
  x,
  y,
  category,
  callbackSubscript,
  isRelated = false,
  borderStyle,
  opacity,
  isActive,
  isHighlighted,
  isDimmed,
  connectedRhymes,
  onMouseEnter,
  onMouseLeave,
  onClick,
}: RhymeBadgeProps) {
  // Build accessible label with category and state info
  const categoryInfo = CATEGORY_DISPLAY[category]
  const relatedNote = isRelated ? ', related' : ''
  const callbackNote = callbackSubscript ? `, callback ${callbackSubscript}` : ''
  const displayLetter = isRelated ? `${letter}′` : letter
  const label = `Rhyme group ${displayLetter}${callbackNote}: ${categoryInfo.name} (${categoryInfo.tier}${relatedNote}). ${connectedRhymes.length} connected rhymes.`

  // Render letter with optional prime symbol and subscript
  const displayText = (
    <>
      {letter}
      {isRelated && <span className="rhyme-badge__prime">′</span>}
      {callbackSubscript && <sub>{callbackSubscript}</sub>}
    </>
  )

  // Build CSS class names
  const letterClass = `rhyme-badge--letter-${letter.toLowerCase()}`
  const stateClasses = [
    isActive && 'rhyme-badge--active',
    isHighlighted && 'rhyme-badge--highlighted',
    isDimmed && 'rhyme-badge--dimmed',
  ]
    .filter(Boolean)
    .join(' ')

  const className = `rhyme-badge ${letterClass} ${stateClasses}`.trim()

  // Border width mapping for inline styles
  const borderWidth = borderStyle === 'solid' ? '2px' : borderStyle === 'dashed' ? '1.5px' : '1px'

  // Event handlers
  const handleMouseEnter = () => {
    onMouseEnter(id)
  }

  const handleMouseLeave = () => {
    onMouseLeave(id)
  }

  const handleClick = (e: MouseEvent) => {
    e.stopPropagation() // Prevent event from bubbling
    onClick(id)
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    // Enter or Space toggles popover
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onClick(id)
    }
  }

  return (
    <div
      className={className}
      style={{
        // ONLY positioning and dynamic properties stay inline
        position: 'absolute',
        left: `${x}px`,
        top: `${y}px`,
        // Dynamic border style (changes based on category strength)
        border: `${borderWidth} ${borderStyle} rgba(255, 255, 255, 0.5)`,
        // Dynamic opacity (changes based on stability)
        opacity: isDimmed ? 0.3 : opacity,
      }}
      role="button"
      tabIndex={0}
      aria-label={label}
      aria-haspopup="dialog"
      aria-expanded={isActive}
      data-testid={`rhyme-badge-${letter}-${category.toLowerCase()}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {displayText}
    </div>
  )
}, arePropsEqual)

export default RhymeBadge
